import { ShoppingBag } from "@mui/icons-material";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import {
  Avatar,
  Box,
  Button,
  Chip,
  Divider,
  Grid,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Typography,
} from "@mui/material";
import moment from "moment";
import React, { useEffect, useState } from "react";
import Chart from "react-apexcharts";
import { Helmet } from "react-helmet";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  getOrderStatusById,
  getOrderTypeLabel,
  PAYMENT_METHODS,
} from "../../constants/enums";
import { SITE_TITLE } from "../../constants/Site";
import { mergeItemsByVariant } from "../../reduxStore/selectors/cartSelectors";
import { getRecentOrders } from "../../reduxStore/slices/dashboardSlice";
import {
  getOrderStats,
  getSalesPaymentSummary,
} from "../../reduxStore/slices/reportSlice";
import { formatCurrency } from "../../utils/currency";
import { useNotify } from "../../utils/snackbarUtils";
import "./Dashboard.css";
import EmptyPaymentMethod from "./EmptyPaymentMethod";
import EmptyRecentOrders from "./EmptyRecentOrders";

function Dashboard(props) {
  const { showSuccess, handleApiError, showError } = useNotify();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { user } = useSelector((state) => state.auth);
  const { recentOrders, loading: isLoading } = useSelector(
    (state) => state.dashboard,
  );
  const {
    orderStats,
    paymentSummary,
    loading: isReportLoading,
  } = useSelector((state) => state.report);
  const storeId = user?.storeId;

  const [dateTime, setDateTime] = useState(moment());

  const handleViewAll = () => {
    navigate(`/app/orders`);
  };

  // Update every second
  useEffect(() => {
    const timer = setInterval(() => {
      setDateTime(moment());
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  // Dynamic greeting
  const hours = dateTime.hour();
  let greeting = "Hello";
  if (hours < 12) greeting = "Good morning";
  else if (hours < 18) greeting = "Good afternoon";
  else greeting = "Good evening";

  // Format date → Monday, 15 July 2025
  const formattedDate = dateTime.format("dddd, D MMMM YYYY");

  // Format time → 04:44 AM
  const formattedTime = dateTime.format("hh:mm A");

  const paymentOptionLabels = Object.values(PAYMENT_METHODS).map(
    (option) => option.label,
  );

  const paymentOptionData = Object.values(PAYMENT_METHODS).map((method) => {
    const summary = paymentSummary.find(
      (item) => item.payment_option_id === method.value,
    );
    return summary?.percentage || 0;
  });

  const options = { labels: paymentOptionLabels };
  const series = paymentOptionData;

  const fetchStats = async () => {
    const startDate = moment().format("YYYY-MM-DD");
    const endDate = moment().format("YYYY-MM-DD");

    try {
      await dispatch(getOrderStats({ storeId, startDate, endDate })).unwrap();
    } catch (err) {
      handleApiError(err);
    }
  };

  const fetchSalesSummary = async () => {
    const startDate = moment().format("YYYY-MM-DD");
    const endDate = moment().format("YYYY-MM-DD");

    try {
      await dispatch(
        getSalesPaymentSummary({ storeId, startDate, endDate }),
      ).unwrap();
    } catch (err) {
      handleApiError(err);
    }
  };

  const fetchRecentOrders = async () => {
    const startDate = moment().format("YYYY-MM-DD");
    const endDate = moment().format("YYYY-MM-DD");
    const offset = 0;
    const limit = 5;

    try {
      await dispatch(
        getRecentOrders({ storeId, startDate, endDate, offset, limit }),
      ).unwrap();
    } catch (err) {
      handleApiError(err);
    }
  };

  // Fetch stats on mount and when storeId changes
  useEffect(() => {
    if (storeId) {
      fetchStats();
      fetchSalesSummary();
      fetchRecentOrders();
    }
  }, [storeId, dispatch]);

  const summary = [
    { title: "Total Orders", value: orderStats.total_orders || "0" },
    {
      title: "Gross Sales",
      value: `${formatCurrency(orderStats.gross_sales || "0")}`,
      color: "rgb(0, 171, 85)",
    },
    {
      title: "Discounts",
      value: `${formatCurrency(orderStats.total_discounts || "0")}`,
      color: "error.main",
    },
    {
      title: "Net Sales",
      value: `${formatCurrency(orderStats.net_sales || "0")}`,
      color: "rgb(0, 171, 85)",
    },
  ];

  const handleRefreshClick = () => {
    fetchRecentOrders();
  };

  return (
    <>
      <Helmet>
        <title> Dashboard | {SITE_TITLE}</title>
      </Helmet>
      <Box className="home-page-content">
        <Box className="home-page-content-inner">
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            px={3}
            py={2}
          >
            <Box>
              <Typography variant="h6" fontWeight="bold">
                {greeting}, {user?.name}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Here is today’s summary
              </Typography>
            </Box>

            <Box textAlign="right">
              <Typography variant="body1" fontWeight="500">
                {formattedDate}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {formattedTime}
              </Typography>
            </Box>
          </Box>

          <Box className="home-count-wrap">
            <Grid container spacing={2} sx={{ mb: 3 }}>
              {summary.map((item, index) => (
                <Box className="report-count-card" key={index}>
                  <Box
                    style={{
                      fontSize: 14,
                      fontWeight: 500,
                    }}
                  >
                    {item.title}
                  </Box>
                  <Box
                    sx={{
                      fontWeight: 700,
                      fontSize: 20,
                      color: item?.color,
                      marginTop: "5px",
                    }}
                  >
                    {item.value}
                  </Box>
                  <Box
                    display="flex"
                    alignItems="center"
                    gap="5px"
                    justifyContent="center"
                  >
                    <Box
                      style={{
                        width: "24px",
                        height: "24px",
                        display: "flex",
                        borderRadius: "50%",
                        position: "relative",
                        justifyContent: "center",
                        alignItems: "center",
                        color: "#118D57",
                        background: "rgba(34 197 94 / 16%)",
                        //   background: "rgba(255 86 48 / 16%)",
                        //   color: "#B71D18",
                      }}
                    >
                      <TrendingUpIcon
                        fontSize="small"
                        sx={{ width: "16px", height: "16px", margin: "0 auto" }}
                      />
                      {/* <TrendingDownIcon
                  fontSize="small"
                  sx={{ width: "16px", height: "16px", margin: "0 auto" }}
                /> */}
                    </Box>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                      }}
                    >
                      <Typography sx={{ fontWeight: 700, mr: "4px" }}>
                        +2.6%
                      </Typography>
                      than yesterday
                    </Typography>
                  </Box>
                </Box>
              ))}
            </Grid>
          </Box>

          <Box className="home-recent-wrap">
            <Box className="home-recent-orders-card">
              <Box className="home-card-heading">Recent Orders</Box>

              <Box>
                {recentOrders && recentOrders.length > 0 ? (
                  <List
                    sx={{
                      width: "100%",
                      bgcolor: "background.paper",
                      borderRadius: "10px",
                    }}
                  >
                    {recentOrders.map((order, idx) => {
                      const dine = order.dine || {};
                      const orderStatus = getOrderStatusById(
                        order.order_status_id,
                      );
                      const allItems = mergeItemsByVariant(order.kots || []);
                      return (
                        <React.Fragment key={order.order_uid}>
                          <ListItem
                            alignItems="flex-start"
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                            }}
                          >
                            <Box display="flex" alignItems="center" gap={1.5}>
                              <ListItemAvatar>
                                <Avatar
                                  variant="rounded"
                                  sx={{ bgcolor: "grey.800" }}
                                >
                                  {dine?.table?.table_name ? (
                                    dine.table.table_name
                                  ) : (
                                    <ShoppingBag sx={{ fontSize: "20px" }} />
                                  )}
                                </Avatar>
                              </ListItemAvatar>
                              <ListItemText
                                primary={
                                  <Box
                                    display="flex"
                                    alignItems="center"
                                    gap={1}
                                  >
                                    <Typography
                                      variant="subtitle1"
                                      fontWeight={600}
                                    >
                                      #{order.order_number} /{" "}
                                      {getOrderTypeLabel(order.order_type_id)}
                                    </Typography>
                                    <Box
                                      sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        gap: "8px",
                                      }}
                                    >
                                      <Chip
                                        label={orderStatus.label}
                                        variant="outlined"
                                        size="small"
                                        sx={{
                                          fontWeight: 500,
                                          color: orderStatus.color,
                                          borderColor: orderStatus.color,
                                        }}
                                      />
                                    </Box>
                                  </Box>
                                }
                                secondary={
                                  <>
                                    <Box
                                      display="flex"
                                      alignItems="center"
                                      gap="10px"
                                    >
                                      {dine?.dine_area_name && (
                                        <Typography
                                          variant="body2"
                                          color="text.secondary"
                                        >
                                          <Chip
                                            label={dine?.dine_area_name}
                                            size="small"
                                          />
                                        </Typography>
                                      )}

                                      <Typography
                                        variant="caption"
                                        color="text.secondary"
                                      >
                                        • {order.created_at}
                                      </Typography>
                                    </Box>
                                  </>
                                }
                              />
                            </Box>

                            {/* Right side (Qty + Total + Buttons) */}
                            <Box
                              display="flex"
                              flexDirection="column"
                              alignItems="flex-end"
                              gap={1}
                            >
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                Qty: {allItems.length} &nbsp; | &nbsp; Total
                                Amount:{" "}
                                <strong>
                                  {formatCurrency(order.total_amount)}
                                </strong>
                              </Typography>
                            </Box>
                          </ListItem>
                          {idx !== recentOrders.length - 1 && (
                            <Divider component="li" />
                          )}
                        </React.Fragment>
                      );
                    })}
                  </List>
                ) : (
                  <EmptyRecentOrders handleRefreshClick={handleRefreshClick} />
                )}
              </Box>

              {recentOrders && recentOrders.length > 0 && !isLoading && (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "flex-end",
                    mt: 1,
                    mb: 1,
                    pr: 2,
                  }}
                >
                  <Button
                    variant="text"
                    size="small"
                    onClick={() => handleViewAll()}
                  >
                    View All →
                  </Button>
                </Box>
              )}
            </Box>

            <Box className="home-orders-by-type">
              <Box className="home-card-heading">Payment Method</Box>
              {paymentSummary.length > 0 && series && series.length > 0 ? (
                <Box>
                  <Chart
                    options={options}
                    series={series}
                    type="pie"
                    width="380"
                  />
                </Box>
              ) : (
                <Box
                  sx={{
                    height: "250px",
                  }}
                >
                  <EmptyPaymentMethod />
                </Box>
              )}
            </Box>
          </Box>
        </Box>
      </Box>
    </>
  );
}

export default Dashboard;
